import React, { memo, useState, useMemo, useEffect } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useParams } from "react-router-dom";
import { uniq, filter, isEmpty, flatMap } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
} from "react-virtualized";
import useStableCallback from "../../../hooks/useStableCallback";
import useRequest from "../../../hooks/useRequest";
import CustomListContainer from "../CustomListContainer";
import Total from "../Total";
import ItemFilter from "../ItemFilter";
import ItemPogSummary from "../ItemPogSummary";
import SettingsForm from "../SettingsForm";
import StickyHeader from "../StickyHeader";
import PermissionsGate from "../../../permissions/PermissionsGate";
import usePermissions from "../../../hooks/usePermissions";
import CustomFloatingButton from "../../../components/CustomFloatingButton";
import CustomDrawer from "../../../components/CustomDrawer";
import FileExport from "../../../components/FileExport";
import CommonDialog from "../../../components/CommonDialog";
import { validPogItem } from "../../../utils/itemValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import {
  getAdjustedQty,
  getTierBasedItemValues,
} from "../../../utils/pogCalculationV2";
import filterHandler from "../../../utils/filterHandler";
import {
  singleValue,
  orderStatus,
  ENTITY_TYPES,
  KeepCacheAsIs,
  prettyNumericRound,
  getMissingValue,
} from "../../../utils";
import {
  showNotifier,
  setGlobalLoader,
  setAdjQtyError,
  setPrevAdjQty,
} from "../../../store/globalSlice";
import {
  prepareInitialData,
  checkItemsBeforeSave,
  prepareSaveData,
  postSaveUpdateData,
  prepareSaveDatesData,
  postSaveDatesUpdateData,
  prepareDeleteDatesData,
  postDeleteDatesUpdateData,
  saveUpdatedItems,
  prepareCreatePOData,
  postCreatePOUpdateData,
  getReleasedItemsWithPONumberMappings,
  checkNonReleasedDuplicateItems,
  prepareInlineSaveData,
  postInlineSaveUpdateData,
} from "./prepareData";
import CustomBreadcrumb from "../CustomBreadcrumb";
import StoreCreditSummary from "../StoreCreditSummary";
import configData from "../../../config.js";
import { SCOPES } from "../../../permissions/permissions-map";
import { useLazyGetItemPogSummaryQuery } from "../../../services/itemPogSummaryApi";
import { useLazyGetTotalQuery } from "../../../services/totalApi";
import CustomNoResult from "../CustomNoResult";
import CustomItem from "../CustomItem";
import CollapsedItemView from "../CollapsedItemView";
import ExpandedItemView from "../ExpandedItemView";
import ReleaseStatusChip from "../ReleaseStatusChip";

const BreadcrumbHeaderSx = { display: "flex", justifyContent: "space-between" };
const OVERSCAN_ROW_COUNT = 10;
const itemCache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 30,
});
export const CALCULATION_FIELDS = [
  "AdjQty",
  "QtyMultiplier",
  "DiscMultiplier",
  "CoreDiscount",
  "NonCoreDiscount",
];

export const recalculateItemValues = (
  item,
  field,
  all = false,
  skipMissing = false
) => {
  let updatedItem = { ...item };
  let AdjQty = updatedItem?.AdjQty;
  if (field === "QtyMultiplier" || all) {
    AdjQty = getAdjustedQty(
      updatedItem?.QtyMultiplier,
      updatedItem?.RecQty,
      skipMissing ? true : getMissingValue(updatedItem),
      updatedItem?.BreakPackType
    );
  }
  const { pogCost, adjCost, totalCost, tier } = getTierBasedItemValues(
    item,
    AdjQty
  );
  return {
    ...updatedItem,
    AdjQty,
    PogCost: Number(prettyNumericRound(pogCost || 0)),
    EachItemCostAfterTier: Number(prettyNumericRound(pogCost || 0)),
    AdjCost: Number(prettyNumericRound(totalCost || 0)),
    ExtendedCost: Number(prettyNumericRound(adjCost || 0)),
    Tier: tier,
  };
};

export const resetItemFieldsOnSelection = (field, item) => {
  if (field === "SelectedItem") {
    let currentItem = { ...item };
    // If selection is made from value 'Y' to 'E'/'N' then reset all values.
    if (item[field] !== singleValue.yes) {
      currentItem = {
        ...currentItem,
        CoreDiscount: 0,
        NonCoreDiscount: 0,
        DatingDays: 0,
        DiscMultiplier: 1,
        QtyMultiplier: 1,
      };
      const calculatedItemValues = recalculateItemValues(
        currentItem,
        field,
        true
      );
      currentItem = {
        ...currentItem,
        Tier: calculatedItemValues?.Tier,
        PogCost: calculatedItemValues?.PogCost,
        AdjQty: 0, // Implicitly mark field as 0 not from calculated values
        AdjCost: 0, // Implicitly mark field as 0 not from calculated values
        ExtendedCost: 0, // Implicitly mark field as 0 not from calculated values
        EachItemCost: calculatedItemValues?.EachItemCost,
        EachItemCostAfterTier: calculatedItemValues?.EachItemCostAfterTier,
        ReleaseDate: null,
        PONumber: "",
      };
      return currentItem;
    }
    const calculatedItemValues = recalculateItemValues(
      currentItem,
      field,
      true
    );
    return {
      ...currentItem,
      AdjQty: calculatedItemValues?.AdjQty, // Implicitly setting default valid value on 'Y' selection
      AdjCost: calculatedItemValues?.AdjCost, // Implicitly setting default valid value on 'Y' selection
      ExtendedCost: calculatedItemValues?.ExtendedCost, // Implicitly setting default valid value on 'Y' selection
      EachItemCost: calculatedItemValues?.EachItemCost, // Implicitly setting default valid value on 'Y' selection
      EachItemCostAfterTier: calculatedItemValues?.EachItemCostAfterTier, // Implicitly setting default valid value on 'Y' selection
    };
  }
  return item;
};

function Items() {
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const { hasPermission } = usePermissions();
  const { departmentLetter, pogId } = useParams();
  const [data, setData] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  // selections are those which items user selected using checkbox, it contains item id only.
  const [selections, setSelections] = useState([]);
  // valid selections are those which shows valid items to be eligible for selection, it contains entire item object.
  const [validSelections, setValidSelections] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const user = useSelector((store) => store.global.user);
  const store = useSelector((store) => store.global.store);
  const itemFilters = useSelector((store) => store.global.itemFilters);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const itemPogSelected = useSelector((store) => store.itemPogSummary.selected);
  const departmentFilters = useSelector(
    (store) => store.global.departmentFilters
  );
  const [getItemPogSummary] = useLazyGetItemPogSummaryQuery();
  const [getTotal] = useLazyGetTotalQuery();

  const isItemsDepartment = isItemDepartment({
    DepartmentLetter: departmentLetter,
  });

  useEffect(() => {
    requestMaker({
      method: "post",
      url: configData.GetPogItemList,
      data: {
        storeId: store.id,
        departmentLetter,
        planogramNumber: pogId,
        vendorName: departmentFilters?.vendorName || null,
        skuNumber: departmentFilters?._sku || null,
      },
    })
      .then((data) => {
        const fetchedData = flatMap(data.data) || [];
        const itemList = prepareInitialData(fetchedData);
        const updatedItems = filterHandler(
          itemFilters,
          itemList,
          itemList,
          ENTITY_TYPES.item
        );
        setValidSelections(updatedItems.filter((item) => validPogItem(item)));
        setFilteredData(updatedItems);
        setData(itemList);
        setOriginalData(itemList);
      })
      .catch((_error) => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        setItemsLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const handleProcessData = useStableCallback(
    (id, _mainData) =>
      ({ field, value }) => {
        const getUpdatedData = (oldItems) => {
          const updatedData = [...oldItems];
          let index = oldItems.findIndex((i) => i?._id === id);
          let item = oldItems[index];
          item = {
            ...resetItemFieldsOnSelection(field, { ...item, [field]: value }),
          };
          item = CALCULATION_FIELDS.includes(field)
            ? recalculateItemValues(item, field)
            : item;
          updatedData[index] = { ...item };
          return updatedData;
        };

        setFilteredData(getUpdatedData);
        setData(getUpdatedData);

        // Handles only Item to Select All auto selection functionality.
        if (field === "selection") {
          if (value) {
            setSelections((oldVal) => {
              const updatedSelections = uniq([...oldVal, id]);
              setSelectAll(updatedSelections.length === validSelections.length);
              return updatedSelections;
            });
          } else {
            setSelections((oldVal) => {
              return filter(oldVal, (itemId) => itemId !== id);
            });
            setSelectAll(false);
          }
        }
      }
  );

  const handleItemLevelSave = useStableCallback((item) => () => {
    const updatedData = prepareInlineSaveData(
      {
        departmentLetter,
        pogId,
      },
      store.id,
      user,
      item,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));

    requestMaker({
      method: "post",
      url: configData.SavePogItemList,
      data: updatedData,
    })
      .then(() => {
        const postSaveUpdatedItems = postInlineSaveUpdateData(
          item,
          filteredData
        );
        setData(saveUpdatedItems(postSaveUpdatedItems));
        setOriginalData(saveUpdatedItems(postSaveUpdatedItems));
        setFilteredData((prevState) => {
          const getUpdates = saveUpdatedItems(postSaveUpdatedItems);
          const _partialUpdates = getUpdates(prevState);
          const updatedData = filterHandler(
            itemFilters,
            _partialUpdates,
            originalData,
            ENTITY_TYPES.item
          );
          return updatedData;
        });
        setSelectAll(false);
        setSelections([]);
        dispatch(showNotifier({ message: "Item details saved successfully" }));
        getTotal({ storeId: store?.id, type: "" });
        getItemPogSummary({
          storeId: store?.id,
          departmentLetter,
          category: null,
          planogramValue: pogId,
          vendorName: null,
          skuNumber: null,
          type: "",
        });
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleSelectAll = useStableCallback((event) => {
    const checked = event?.target?.checked;
    setFilteredData((oldData) => {
      return oldData?.map((item) => {
        if (validPogItem(item)) {
          checked && setSelections((oldSel) => uniq([...oldSel, item?._id]));
          return {
            ...item,
            selection: checked,
          };
        }
        return item;
      });
    });

    setData((oldData) => {
      return oldData?.map((item) => {
        // Only filtered item set selection value.
        const itemFound = filteredData.find((fi) => fi?._id === item?._id);
        if (itemFound && validPogItem(item)) {
          checked && setSelections((oldSel) => uniq([...oldSel, item?._id]));
          return {
            ...item,
            selection: checked,
          };
        }
        return item;
      });
    });

    if (!checked) {
      setSelections([]);
    }
    setSelectAll(!selectAll);
  });

  const toggleDrawer = useStableCallback((open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setIsDrawerOpen(open);
  });

  const handleFilter = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      data,
      originalData,
      ENTITY_TYPES.item
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    itemCache.clearAll();
    setValidSelections(validItems);
    setFilteredData(updatedData);

    const selectionCount = updatedData.filter((i) => !!i?.selection);
    if (
      selectionCount.length === selections.length &&
      selections.length === updatedData.length &&
      selections.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  });

  const handleFilterReset = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      data,
      originalData,
      ENTITY_TYPES.item
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    itemCache.clearAll();
    setValidSelections(validItems);
    setFilteredData(updatedData);
    if (selectAll) {
      const selectionCount = updatedData.filter((i) => i?.selection);
      if (
        selectionCount.length === selections.length &&
        selections.length === updatedData.length &&
        selections.length
      ) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
    }
  });

  const handleSortChange = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      filteredData,
      originalData,
      ENTITY_TYPES.item
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    setValidSelections(validItems);
    setFilteredData(KeepCacheAsIs(filteredData, updatedData));
  });

  const handleSave = useStableCallback((formData) => {
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      formData.selected,
      filteredData,
      selections,
      originalData
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareSaveData(
      {
        departmentLetter,
        pogId,
      },
      formData,
      store.id,
      user,
      filteredData,
      selections,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));

    requestMaker({
      method: "post",
      url: configData.SavePogItemList,
      data: updatedData,
    })
      .then(() => {
        const postSaveUpdatedItems = postSaveUpdateData(
          formData,
          filteredData,
          originalData,
          selections
        );
        setData(saveUpdatedItems(postSaveUpdatedItems));
        setOriginalData(saveUpdatedItems(postSaveUpdatedItems));
        setFilteredData((prevState) => {
          const getUpdates = saveUpdatedItems(postSaveUpdatedItems);
          const _partialUpdates = getUpdates(prevState);
          const updatedData = filterHandler(
            itemFilters,
            _partialUpdates,
            originalData,
            ENTITY_TYPES.item
          );
          return updatedData;
        });
        setSelectAll(false);
        setSelections([]);
        dispatch(showNotifier({ message: "Item details saved successfully" }));
        getTotal({ storeId: store?.id, type: "" });
        getItemPogSummary({
          storeId: store?.id,
          departmentLetter,
          category: null,
          planogramValue: pogId,
          vendorName: null,
          skuNumber: null,
          type: "",
        });
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleCreatePONumbers = useStableCallback((details) => {
    if (isItemsDepartment) {
      const isValidItemSelection = checkNonReleasedDuplicateItems(
        details,
        getReleasedItemsWithPONumberMappings(filteredData),
        filteredData,
        selections
      );
      if (!isValidItemSelection) {
        dispatch(
          showNotifier({
            message:
              "Can not generate PO number for selected items, since same PO number is not allowed for multiple items having same item number.",
            severity: "error",
          })
        );
        return false;
      }
    }
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareCreatePOData(
      {
        departmentLetter,
        pogId,
      },
      store.id,
      details,
      user?.email,
      selections,
      filteredData,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.CreateItemPONumber,
      data: updatedData,
    })
      .then((data) => {
        const postDeleteUpdatedItems = postCreatePOUpdateData(
          filteredData,
          data?.data,
          selections
        );
        setFilteredData(saveUpdatedItems(postDeleteUpdatedItems, true));
        setData(saveUpdatedItems(postDeleteUpdatedItems, true));
        setOriginalData(saveUpdatedItems(postDeleteUpdatedItems, true));
        dispatch(
          showNotifier({
            message: "Saved changes to PO number type.",
          })
        );
        getItemPogSummary({
          storeId: store?.id,
          departmentLetter,
          category: null,
          planogramValue: pogId,
          vendorName: null,
          skuNumber: null,
          type: "",
        });
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not generate PO number for selected items.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  });

  const checkPOGenerated = useStableCallback(() => {
    let isAllPOGenerated = true;
    const poNumbers = filteredData
      ?.map((i) => {
        if (selections.includes(i?._id) && !!i?.PONumber) {
          return i._id;
        }
        return undefined;
      })
      ?.filter((i) => !!i);
    if (poNumbers.length === selections.length) {
      isAllPOGenerated = true;
    } else {
      isAllPOGenerated = false;
    }
    return isAllPOGenerated;
  });

  const handleSaveDates = useStableCallback((releaseDate) => {
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    if (!checkPOGenerated()) {
      return dispatch(
        showNotifier({
          message:
            "PO Number for some selected items are missing. Please generate PO Number.",
          severity: "error",
        })
      );
    }
    const updatedData = prepareSaveDatesData(
      {
        departmentLetter,
        pogId,
      },
      releaseDate,
      store.id,
      user?.email,
      selections,
      filteredData,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));

    requestMaker({
      method: "post",
      url: configData.SaveItemReleaseDate,
      data: updatedData,
    })
      .then(() => {
        const postSaveUpdatedItems = postSaveDatesUpdateData(
          releaseDate,
          filteredData,
          originalData,
          selections
        );
        setFilteredData(saveUpdatedItems(postSaveUpdatedItems));
        setData(saveUpdatedItems(postSaveUpdatedItems));
        setOriginalData(saveUpdatedItems(postSaveUpdatedItems));
        setSelectAll(false);
        setSelections([]);
        dispatch(
          showNotifier({ message: "Release Dates saved successfully." })
        );
        getItemPogSummary({
          storeId: store?.id,
          departmentLetter,
          category: null,
          planogramValue: pogId,
          vendorName: null,
          skuNumber: null,
          type: "",
        });
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not save the date for selected items.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleDeleteDates = useStableCallback(() => {
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareDeleteDatesData(
      {
        departmentLetter,
        pogId,
      },
      store.id,
      user.email,
      selections,
      filteredData,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.DeleteItemReleaseDate,
      data: updatedData,
    })
      .then(() => {
        const postDeleteUpdatedItems = postDeleteDatesUpdateData(
          filteredData,
          originalData,
          selections
        );
        setFilteredData(saveUpdatedItems(postDeleteUpdatedItems));
        setData(saveUpdatedItems(postDeleteUpdatedItems));
        setOriginalData(saveUpdatedItems(postDeleteUpdatedItems));
        setSelectAll(false);
        setSelections([]);
        dispatch(
          showNotifier({ message: "Release Dates deleted successfully." })
        );
        getItemPogSummary({
          storeId: store?.id,
          departmentLetter,
          category: null,
          planogramValue: pogId,
          vendorName: null,
          skuNumber: null,
          type: "",
        });
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not delete the date for selected items.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleItemReset = useStableCallback(() => {
    setShowResetDialog(true);
  });

  const handleResetDialogClose = useStableCallback(() => {
    setShowResetDialog(false);
  });

  const handleResetDialogDone = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      originalData,
      originalData,
      ENTITY_TYPES.item
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    setValidSelections(validItems);
    setFilteredData(() => KeepCacheAsIs(filteredData, updatedData));
    setData(() => originalData);
    setSelectAll(false);
    setSelections([]);
    if (adjQtyError) {
      dispatch(setAdjQtyError(null));
      dispatch(setPrevAdjQty(null));
    }
    handleResetDialogClose();
    window.scrollTo(0, 0);
  });

  const hideSettings = useMemo(() => {
    const isSelectedItemFound = filteredData.find((i) =>
      selections.includes(i?._id)
    );
    return isEmpty(selections) || Boolean(adjQtyError) || !isSelectedItemFound;
  }, [selections, adjQtyError, filteredData]);

  const isRowLoaded = ({ index }) =>
    index < filteredData.length && !filteredData[index]?.virtualLoading;

  const loadMoreRows = ({ startIndex, stopIndex }) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const d = [...filteredData];
        for (let idx = startIndex; idx <= stopIndex; idx++) {
          d[idx].virtualLoading = false;
        }
        setFilteredData(d);
        resolve();
      }, 800);
    });
  };

  const listRenderer = ({ index, key, style, parent }) => {
    const item = filteredData[index] || {};
    return (
      <CellMeasurer
        key={key}
        cache={itemCache}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ registerChild, measure }) => (
          <div style={style} key={key} ref={registerChild}>
            <CustomItem
              key={item?._id}
              onChange={measure}
              item={item}
              collapsedView={
                <CollapsedItemView
                  data={item}
                  handleProcessData={handleProcessData(item?._id, originalData)}
                  freezeItem={itemPogSelected !== singleValue.yes}
                />
              }
              expandedView={
                <ExpandedItemView
                  data={item}
                  handleProcessData={handleProcessData(item?._id, originalData)}
                  handleItemLevelSave={handleItemLevelSave(item)}
                  releaseStatus={
                    <ReleaseStatusChip
                      status={item?.ReleaseStatus === orderStatus.released}
                    />
                  }
                />
              }
            />
          </div>
        )}
      </CellMeasurer>
    );
  };

  const memoizedHideSelectAll = useMemo(() => {
    return (
      store?.pending ||
      store?.isArchived ||
      store?.isCompleted ||
      store?.dataguideInProgress ||
      !hasPermission({ scopes: [SCOPES.canEdit] }) ||
      filteredData.length <= 1 ||
      itemPogSelected !== singleValue.yes
    );
  }, [
    hasPermission,
    store?.pending,
    itemPogSelected,
    store?.isArchived,
    store?.isCompleted,
    store?.dataguideInProgress,
    filteredData.length,
  ]);

  return (
    <Container id="page-container" maxWidth={false} sx={{ paddingY: 1 }}>
      <Stack spacing={1}>
        <Box sx={BreadcrumbHeaderSx}>
          <CustomBreadcrumb entityType={ENTITY_TYPES.item} />
          <StoreCreditSummary />
        </Box>
        <Total />
        <ItemFilter
          disableFilters={itemsLoading || adjQtyError}
          handleFilter={handleFilter}
          handleReset={handleFilterReset}
          handleSortChange={handleSortChange}
        />
        <ItemPogSummary />
        <CustomListContainer
          isResultPresent={!!filteredData.length}
          selectAll={validSelections.length ? selectAll : false}
          handleSelectAll={handleSelectAll}
          disableSelectAll={!validSelections.length}
          resultLoading={itemsLoading}
          hideSelectAll={memoizedHideSelectAll}
          entityType={ENTITY_TYPES.item}
          exportButton={<FileExport data={filteredData} type="osoItem" />}
          stickyHeader={<StickyHeader entityType={ENTITY_TYPES.item} />}
          pagination={<></>}
          paginationTotals={<></>}
          resetAction={
            <PermissionsGate scopes={[SCOPES.canEdit]}>
              {!itemsLoading &&
                !!filteredData.length &&
                itemPogSelected === singleValue.yes && (
                  <>
                    <Button
                      variant="outlined"
                      sx={{ alignSelf: "flex-end" }}
                      onClick={handleItemReset}
                      size="small"
                      disabled={
                        store?.pending ||
                        store?.isArchived ||
                        store?.isCompleted ||
                        store?.dataguideInProgress
                      }
                    >
                      Reset
                    </Button>
                    <CommonDialog
                      open={showResetDialog}
                      title="Reset All Unsaved Items"
                      content={
                        "Are you sure, you want to reset/revert all unsaved item changes?"
                      }
                      type="yesNo"
                      handleClose={handleResetDialogClose}
                      handleDone={handleResetDialogDone}
                    />
                  </>
                )}
            </PermissionsGate>
          }
        >
          {filteredData.length ? (
            <div className="_virtual-container">
              <div className="_virtual-list-wrapper">
                <InfiniteLoader
                  isRowLoaded={isRowLoaded}
                  loadMoreRows={loadMoreRows}
                  rowCount={filteredData.length}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                      {({ width, height }) => (
                        <List
                          width={width}
                          height={height}
                          ref={registerChild}
                          deferredMeasurementCache={itemCache}
                          rowHeight={itemCache.rowHeight}
                          rowRenderer={listRenderer}
                          onRowsRendered={onRowsRendered}
                          rowCount={filteredData.length}
                          overscanRowCount={OVERSCAN_ROW_COUNT}
                        />
                      )}
                    </AutoSizer>
                  )}
                </InfiniteLoader>
              </div>
            </div>
          ) : (
            <CustomNoResult entity={ENTITY_TYPES.item} loading={itemsLoading} />
          )}
        </CustomListContainer>
      </Stack>
      {!hideSettings && <CustomFloatingButton onClick={toggleDrawer(true)} />}
      <CustomDrawer open={isDrawerOpen} onClose={toggleDrawer(false)}>
        <SettingsForm
          onSave={handleSave}
          onCreatePONumbers={handleCreatePONumbers}
          onSaveDates={handleSaveDates}
          onDeleteDates={handleDeleteDates}
          onClose={toggleDrawer(false)}
          isItemsDepartment={isItemsDepartment}
        />
      </CustomDrawer>
    </Container>
  );
}

export default memo(Items);
